import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { DataSourceFieldUtils } from '../controls/data-source/data-source-field-utils';
import { DataSourceOption, DataSourceOptions } from '../controls/data-source/data-source-options';
import { DictionaryItem } from '../../interfaces/dictionary-item.interface';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PredefinedValue } from '../../interfaces/predefined-value.interface';
import { ReferenceField } from '../../interfaces/reference-field.interface';
import { SmartFilterData, SmartSelectOption } from '../../interfaces/smart-filter-data.interface';
import { MatOption } from '@angular/material';
import { DataSourceOptionsService } from '../../services/data-source-options/data-source-options.service';
import { ConfigServiceProvider } from '../../services/config/config.service';

@Component({
  selector: 'forms-smart-component',
  templateUrl: './smart-filter.component.html',
  })
export class SmartFilterComponent implements OnInit {
  constructor(
    @Inject(MatDialogRef) private dialogRef: MatDialogRef<SmartFilterComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SmartFilterData,
    @Inject(DataSourceOptionsService) private dataSourceOptionsService: DataSourceOptionsService,
    @Inject(ConfigServiceProvider) private configService: ConfigServiceProvider,
  ) {}
  readonly selectAllOption: SmartSelectOption<DataSourceOption> = {
    visible: true,
    option: null,
  };

  @ViewChild('allSelected') private allSelected: MatOption;

  searchInputControl = new FormControl();
  multiSelectControl = new FormControl();
  selectedOptions: SmartSelectOption<DataSourceOption>[] = [];
  displayedOptions: SmartSelectOption<DataSourceOption>[] = [];

  ngOnInit() {
    this.initMultiSelect();
    this.initSearchInput();

    const selectedValues = this.data.selectedValues || [];
    this.displayedOptions = this.data.formField.values;
    const selectedOptions = selectedValues.map((v) => this.displayedOptions.find((d) => DataSourceFieldUtils.compareValues(d.option, v)));
    this.multiSelectControl.setValue(selectedOptions);
  }

  private initMultiSelect(): void {
    this.multiSelectControl.valueChanges.subscribe((selectedValues: SmartSelectOption<DataSourceOption>[]) => {
      const selectedInvisibleOptions = this.selectedOptions.filter((so) => so.visible === false);
      const currentlySelectedVisibleOptions = selectedValues.filter((so) => so.option !== null);
      this.selectedOptions = [...selectedInvisibleOptions, ...currentlySelectedVisibleOptions];
      this.handleSelectAllOption();
    });
  }

  private initSearchInput(): void {
    this.searchInputControl.valueChanges.subscribe((value) => this.changeOptionVisiblity(value));
  }

  private changeOptionVisiblity(searchPhase: string): void {
    const filterValue = searchPhase.toLowerCase();

    switch (this.data.formField.dataSource) {
      case 'dataView':
        this.getDataViewValues(searchPhase);
        break;
      case 'dictionary':
        (this.displayedOptions as SmartSelectOption<DictionaryItem>[]).forEach((option) => this.markOptionAsVisible(option, 'name', filterValue));
        this.handleSelectedValues(searchPhase);
        break;
      case 'predefined':
        (this.displayedOptions as SmartSelectOption<PredefinedValue>[]).forEach((option) =>
          this.markOptionAsVisible(option, 'dataViewColumn', filterValue),
        );
        this.handleSelectedValues(searchPhase);
        break;
    }
  }

  private getDataViewValues(searchPhase: string): void {
    this.dataSourceOptionsService.getReferenceValue(this.data.formField, this.configService.getBackendBaseAPI(), searchPhase).subscribe((values) => {
      this.displayedOptions = values.map((v) => ({ visible: true, option: v }));
      this.selectedOptions.forEach((so) => (so.visible = values.some((v) => so.option.id === v.id)));
      this.handleSelectedValues(searchPhase);
    });
  }

  private markOptionAsVisible(smartOption: SmartSelectOption<DataSourceOption>, fieldName: string, value: string): void {
    if (!smartOption.option[fieldName]) {
      return;
    }
    smartOption.visible = smartOption.option[fieldName].toLocaleLowerCase().includes(value);
  }

  private handleSelectedValues(searchPhase: string): void {
    const selectedVisibleOptions = this.selectedOptions.filter((so) => so.visible === true);
    if (
      this.areSelectedOptionsNotVisible(selectedVisibleOptions.length, this.selectedOptions.length) ||
      this.isFilterValueEmptyAndAllSelectedOptionsVisible(searchPhase, selectedVisibleOptions, this.selectedOptions)
    ) {
      this.multiSelectControl.setValue(selectedVisibleOptions);
    }

    this.handleSelectAllOption();
  }

  private areSelectedOptionsNotVisible(selectedVisibleOptionsLength: number, selectedOptionsLength: number): boolean {
    return selectedVisibleOptionsLength !== selectedOptionsLength;
  }

  private isFilterValueEmptyAndAllSelectedOptionsVisible(
    filterValue: string,
    selectedVisibleOptions: SmartSelectOption<DataSourceOption>[],
    selectedOptions: SmartSelectOption<DataSourceOption>[],
  ): boolean {
    return filterValue === '' && selectedVisibleOptions.length === selectedOptions.length;
  }

  onSelectOpenedChange(isOpened: boolean): void {
    if (isOpened) {
      this.handleSelectAllOption();
    } else {
      this.searchInputControl.setValue('');
    }
  }

  private handleSelectAllOption(): void {
    if (!this.allSelected) {
      return;
    }

    if (this.areAllValuesSelected(this.selectedOptions) && !this.allSelected.selected) {
      this.allSelected.select();
    }

    if (!this.areAllValuesSelected(this.selectedOptions) && this.allSelected.selected) {
      this.allSelected.deselect();
    }
  }

  public compareFn: ((o1: any, o2: any) => boolean) | null = (
    o1: SmartSelectOption<DictionaryItem | ReferenceField | PredefinedValue>,
    o2: SmartSelectOption<DictionaryItem | ReferenceField | PredefinedValue> | DictionaryItem | ReferenceField | PredefinedValue,
  ): boolean => {
    const providedValue = this.isSmartSelectOption(o2)
      ? (o2 as SmartSelectOption<DictionaryItem | ReferenceField | PredefinedValue>).option
      : (o2 as DictionaryItem | ReferenceField | PredefinedValue);
    return DataSourceFieldUtils.compareValues(o1.option, providedValue);
  };

  private isSmartSelectOption(object: any): boolean {
    return 'visible' in object && 'option' in object && Object.keys(object).length === 2;
  }

  onSearchInputClear(): void {
    this.searchInputControl.patchValue('');
  }

  noOptionVisible(): boolean {
    return this.displayedOptions.every((so) => so.visible === false);
  }

  onPickAllButtonChanged(): void {
    if (!this.areAllValuesSelected(this.selectedOptions)) {
      this.multiSelectControl.setValue(this.displayedOptions);
    } else {
      this.multiSelectControl.setValue([]);
    }
  }

  private areAllValuesSelected(selectedValues: SmartSelectOption<DataSourceOption>[]): boolean {
    return selectedValues.length === this.displayedOptions.length;
  }

  closeDialog(returnValue: boolean = false): void {
    if (!returnValue) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close(this.getSelectedOptions());
  }

  private getSelectedOptions(): DataSourceOptions {
    return this.selectedOptions.map((so) => so.option);
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_forms/projects/forms/src/lib/components/smart-component/smart-filter.component.ts